import React, { useState, useEffect, useRef } from "react";
import { 
  Plus, 
  Edit, 
  Settings2,
  Trash2,
  ChevronDown,
  Check
} from "lucide-react";
import {
  saveConnector,
  getConnectorInfo,
  updateConnectorStatus,
  getConnectorTypes // Add this import
} from "../../../../service/apiService";

// Add interface for connector type
interface ConnectorType {
  name: string;
  value: string;
}

const ErrorAlert: React.FC<{ message: string; onClose: () => void }> = ({ message, onClose }) => (
  <div className="mb-4 p-2 sm:p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm sm:text-base flex justify-between items-center">
    <span>{message}</span>
    <button onClick={onClose} className="text-red-600 hover:text-red-800 ml-2">×</button>
  </div>
);

const SuccessAlert: React.FC<{ message: string; onClose: () => void }> = ({ message, onClose }) => (
  <div className="mb-4 p-2 sm:p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm sm:text-base flex justify-between items-center">
    <span>{message}</span>
    <button onClick={onClose} className="text-green-600 hover:text-green-800 ml-2">×</button>
  </div>
);

// Custom Dropdown Component
interface CustomDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: ConnectorType[];
  placeholder: string;
  disabled?: boolean;
  loading?: boolean;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  loading = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setHoveredIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(option => option.value === value);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setHoveredIndex(-1);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disabled || loading) return;

    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else if (hoveredIndex >= 0) {
          handleSelect(options[hoveredIndex].value);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHoveredIndex(-1);
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHoveredIndex(prev => (prev < options.length - 1 ? prev + 1 : 0));
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (isOpen) {
          setHoveredIndex(prev => (prev > 0 ? prev - 1 : options.length - 1));
        }
        break;
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && !loading && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={disabled || loading}
        className={`
          w-full px-2 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm 
          border border-gray-300 rounded-lg bg-white text-left
          focus:ring-2 focus:ring-purple-500 focus:border-purple-500
          transition-colors flex items-center justify-between
          ${disabled || loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-gray-400'}
        `}
      >
        <span className={selectedOption ? 'text-gray-900' : 'text-gray-500'}>
          {selectedOption ? selectedOption.name : placeholder}
        </span>
        <ChevronDown 
          className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      {isOpen && !disabled && !loading && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
          {options.length === 0 ? (
            <div className="px-3 py-2 text-xs sm:text-sm text-gray-500">No options available</div>
          ) : (
            options.map((option, index) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option.value)}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(-1)}
                className={`
                  w-full px-3 py-2 text-xs sm:text-sm text-left transition-colors
                  flex items-center justify-between
                  ${hoveredIndex === index ? 'bg-purple-600 text-white' : 'text-gray-900 hover:bg-purple-600 hover:text-white'}
                  ${value === option.value ? 'bg-purple-100 text-purple-900' : ''}
                  ${hoveredIndex === index && value === option.value ? 'bg-purple-600 text-white' : ''}
                `}
              >
                <span>{option.name}</span>
                {value === option.value && (
                  <Check className={`w-4 h-4 ${hoveredIndex === index ? 'text-white' : 'text-purple-600'}`} />
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const ViewConnector: React.FC = () => {
  const [connectors, setConnectors] = useState<any[]>([]);
  const [connectorTypes, setConnectorTypes] = useState<ConnectorType[]>([]); // Add state for connector types
  const [loading, setLoading] = useState(true);
  const [typesLoading, setTypesLoading] = useState(false); // Add loading state for types
  const [toggleLoading, setToggleLoading] = useState<string | null>(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingConnector, setEditingConnector] = useState<any | null>(null);

  const [formData, setFormData] = useState({
    name: "",
    type: ""
  });

  useEffect(() => {
    fetchConnectors();
    fetchConnectorTypes(); // Fetch connector types on component mount
  }, []);

  const fetchConnectors = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getConnectorInfo();
      setConnectors(response.connectors || []);
    } catch (err: any) {
      setError("Failed to fetch connectors");
      console.error("Error fetching connectors:", err);
    } finally {
      setLoading(false);
    }
  };

  // Add new function to fetch connector types
  const fetchConnectorTypes = async () => {
    try {
      setTypesLoading(true);
      const response = await getConnectorTypes();
      setConnectorTypes(response.connectors || []);
    } catch (err: any) {
      console.error("Error fetching connector types:", err);
      // Set fallback data if API fails
      setConnectorTypes([
        { name: "CanLII Legal Database", value: "canlii" },
        { name: "Alpha Vantage Financial Data", value: "alpha-vantage" },
        { name: "Microsoft Teams", value: "microsoft-teams" },
        { name: "Nova Act Browser Automation", value: "nova-act" }
      ]);
    } finally {
      setTypesLoading(false);
    }
  };

  // Helper function to get connector type display name
  const getConnectorTypeName = (typeValue: string) => {
    const connectorType = connectorTypes.find(ct => ct.value === typeValue);
    return connectorType ? connectorType.name : typeValue;
  };

  const handleToggle = async (connectorId: string, currentStatus: string) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    
    try {
      setToggleLoading(connectorId);
      setError(null);
      await updateConnectorStatus(connectorId, newStatus);
      setSuccess(`Connector ${newStatus === "active" ? "activated" : "deactivated"} successfully`);
      await fetchConnectors();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to update connector status");
    } finally {
      setToggleLoading(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.type) {
      setError("All fields are required");
      return;
    }

    try {
      setSaveLoading(true);
      setError(null);
      await saveConnector(formData);
      setSuccess(editingConnector ? "Connector updated successfully" : "Connector saved successfully");
      setShowForm(false);
      setEditingConnector(null);
      setFormData({ name: "", type: "" });
      await fetchConnectors();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to save connector");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleEdit = (connector: any) => {
    setFormData({
      name: connector.name,
      type: connector.type
    });
    setEditingConnector(connector);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({ name: "", type: ""});
    setEditingConnector(null);
    setShowForm(false);
    setError(null);
  };

  if (loading && !toggleLoading && !saveLoading) {
    return (
      <div className="p-2 sm:p-4">
        <div className="animate-pulse space-y-2 sm:space-y-4">
          {[1, 2, 3].map((n) => (
            <div key={n} className="h-16 sm:h-20 bg-gray-100 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-2 sm:p-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2 sm:mb-4 gap-2">
        <h2 className="text-base sm:text-lg font-semibold">Connector Management</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-purple-500 hover:bg-purple-700 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg flex items-center gap-2 transition-colors text-sm sm:text-base"
        >
          <Plus size={14} className="sm:w-4 sm:h-4" />
          Add Connector
        </button>
      </div>
      
      {error && <ErrorAlert message={error} onClose={() => setError(null)} />}
      {success && <SuccessAlert message={success} onClose={() => setSuccess(null)} />}

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 shadow-sm mb-4">
          <h3 className="text-sm sm:text-base font-semibold mb-3 sm:mb-4">
            {editingConnector ? "Edit Connector" : "Add New Connector"}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Connector Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-2 py-1.5 sm:px-3 sm:py-2 bg-white text-gray-900 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="e.g., Production OpenAI"
                  required
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Connector Type *
                </label>
                <CustomDropdown
                  value={formData.type}
                  onChange={(value) => setFormData({ ...formData, type: value })}
                  options={connectorTypes}
                  placeholder={typesLoading ? "Loading connector types..." : "Select connector type..."}
                  disabled={!!editingConnector}
                  loading={typesLoading}
                />
                {editingConnector && (
                  <p className="text-xs text-gray-500 mt-1">Type cannot be changed when editing</p>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 sm:gap-3">
              <button
                type="button"
                onClick={resetForm}
                className="px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saveLoading || typesLoading}
                className="px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {saveLoading ? "Saving..." : editingConnector ? "Update" : "Save"}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-2 sm:space-y-4">
        {connectors.filter(connector => connector.name && connector.type).length === 0 ? (
          <div className="text-center py-8 sm:py-12">
            <Settings2 size={32} className="mx-auto text-gray-400 mb-3 sm:mb-4 w-8 h-8 sm:w-12 sm:h-12" />
            <h3 className="text-sm sm:text-base font-medium text-gray-900 mb-1 sm:mb-2">No connectors found</h3>
            <p className="text-xs sm:text-sm text-gray-600">Get started by adding your first connector</p>
          </div>
        ) : (
          connectors
            .filter(connector => connector.name && connector.type) 
            .map(connector => (
            <div 
              key={connector.id}
              className="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:items-center justify-between p-2 sm:p-4 rounded-lg border border-gray-200 hover:bg-gray-50"
            >
              <div className="flex items-start sm:items-center space-x-2 sm:space-x-3 w-full sm:w-auto min-w-0">
                <Settings2 className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 mt-1 sm:mt-0 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                    <h4 className="font-medium text-sm sm:text-base break-words">{connector.name || 'Unknown'}</h4>
                    <span className={`text-xs sm:text-sm px-2 py-0.5 rounded-full inline-flex items-center w-fit ${
                      connector.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {connector.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-500 mt-1">
                    <span className="font-semibold text-gray-600">Type:</span> {getConnectorTypeName(connector.type) || 'Unknown'}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3">
                <div className="flex items-center justify-end relative w-12 sm:w-11">
                  {toggleLoading === connector.id ? (
                    <div className="w-4 h-4 sm:w-5 sm:h-5">
                      <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  ) : (
                    <label className="relative inline-flex items-centers cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={connector.status === 'active'}
                        onChange={() => handleToggle(connector.id, connector.status || 'inactive')}
                      />
                      <div className={`
                        w-9 sm:w-11 h-5 sm:h-6 bg-gray-200 
                        peer-focus:outline-none peer-focus:ring-4 
                        peer-focus:ring-purple-300 rounded-full peer 
                        peer-checked:after:translate-x-full 
                        peer-checked:after:border-white 
                        after:content-[''] after:absolute 
                        after:top-[2px] after:left-[2px] 
                        after:bg-white after:border-gray-300 
                        after:border after:rounded-full 
                        after:h-4 sm:after:h-5 after:w-4 sm:after:w-5 
                        after:transition-all 
                        peer-checked:bg-purple-600
                      `}></div>
                    </label>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ViewConnector;